import { Menu } from 'lucide-react';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { useUserPermissions } from '@/hooks/use-user-permissions';
import { ROUTES } from '@/lib/constants/routes';
import { useUiStore } from '@/store/ui.store';

function getInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      const first = parts[0]?.charAt(0) ?? '';
      const last = parts[parts.length - 1]?.charAt(0) ?? '';
      return `${first}${last}`.toUpperCase();
    }
    if (parts.length === 1 && parts[0] && parts[0].length >= 2) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    if (parts.length === 1 && parts[0]) {
      return parts[0].charAt(0).toUpperCase();
    }
  }
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  return 'U';
}

/**
 * Fixed top bar aligned with the sidebar offset (planning-v2 style).
 */
export function TopNav(): ReactElement {
  const { user } = useAuth();
  const isMobile = useUiStore((s) => s.isMobile);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);

  const { isBookingRequester } = useUserPermissions();
  const supplierSubtitle =
    isBookingRequester && user?.bookingRequester?.name?.trim()
      ? user.bookingRequester.name.trim()
      : null;

  return (
    <header
      className="fixed right-0 top-0 z-40 h-[68px] border-b bg-white transition-all duration-500 ease-in-out"
      style={{ left: isMobile ? '0' : '60px' }}
    >
      <div className="flex h-full items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-4">
          {isMobile ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="touch-target shrink-0 lg:hidden"
            >
              <Menu className="h-5 w-5" aria-hidden />
              <span className="sr-only">Open menu</span>
            </Button>
          ) : null}
          <div className="flex min-w-0 flex-col justify-center gap-0 leading-tight">
            <Link
              to={ROUTES.BOOKING_REQUESTS}
              onClick={() => {
                if (isMobile) {
                  setSidebarOpen(false);
                }
              }}
              className="min-w-0 truncate text-left text-sm font-medium leading-tight text-[#1E293B] hover:text-[#0f172a] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#05BFA0] sm:text-base"
              aria-label="Supplier Portal — go to booking requests"
            >
              Supplier Portal
            </Link>
            {supplierSubtitle ? (
              <span
                className="mt-0.5 truncate text-[11px] font-normal leading-tight text-[#64748B] sm:text-xs"
                title={supplierSubtitle}
              >
                {supplierSubtitle}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#6366F1] text-xs font-semibold text-white sm:h-10 sm:w-10 sm:text-sm"
            title={user?.email ?? user?.name ?? 'User'}
          >
            {user ? getInitials(user.name, user.email) : 'U'}
          </span>
        </div>
      </div>
    </header>
  );
}
